import React, { useState, useRef, useCallback } from 'react';
import Modal from 'global/components/modal';
import Form from 'global/components/form';
import {
  TextField, MenuItem, Select, FormControl, InputLabel,
  IconButton, Icon,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Text } from 'global/components/text';
import { Button } from 'global/components/Button';
import { resolveSelectSubType, SELECT_SUB_TYPE_LABELS, getCategoryByComponent, NESTED_FIELD_TYPES } from './constants';
import { get, set } from 'global/lib/lodash';
import { ConfigForm } from './styles';

const deepClone = (obj) => JSON.parse(JSON.stringify(obj));

// The Form component's ToggleButton onChange guard (`val && updateState(...)`) prevents
// setting falsy values like `false` or `''`. Use string values and convert on save.
const toBoolStr = (v) => (v ? 'yes' : 'no');
const fromBoolStr = (v) => v === 'yes';

// --- Nested field config for a single repeater sub-field ---
const NestedFieldConfig = ({ field, index, onChange, onRemove }) => {
  const theme = useTheme();
  const fieldType = field.type || 'Text';
  const onChangeRef = useRef(onChange);
  const metaRef = useRef({ index, fieldType });
  onChangeRef.current = onChange;
  metaRef.current = { index, fieldType };

  const handleFormState = useCallback((vals) => {
    const { index: idx, fieldType: ft } = metaRef.current;
    const updated = { type: ft, label: vals.label || '', config: {} };
    // The Form's updateState uses lodash set() which creates nested objects
    // for dot-separated IDs (e.g. 'config.options' → { config: { options } }).
    // So vals.config is an object, not flat 'config.*' keys.
    const configVals = vals.config || {};
    Object.keys(configVals).forEach((configKey) => {
      let v = configVals[configKey];
      if (v === 'yes' || v === 'no') v = fromBoolStr(v);
      // Convert ChipAdd string arrays back to objects
      if (configKey === 'options' && Array.isArray(v)) {
        v = ft === 'Toggle'
          ? v.map((s) => ({ title: s, value: s.toLowerCase().replace(/\s+/g, '_') }))
          : v.map((s) => ({ label: s, value: s.toLowerCase().replace(/\s+/g, '_') }));
      }
      updated.config[configKey] = v;
    });
    onChangeRef.current(idx, updated);
  }, []);

  const getNestedInputs = () => {
    const config = field.config || {};
    const inputs = [
      {
        id: 'label',
        type: 'TextField',
        defaultValue: () => field.label || '',
        props: { label: 'Label' },
      },
      {
        id: 'config.helper_text',
        type: 'TextField',
        defaultValue: () => get(config, 'helper_text', ''),
        props: { label: 'Helper Text' },
      },
    ];

    if (fieldType === 'Text') {
      inputs.push({
        id: 'config.sub_type',
        type: 'ToggleButton',
        options: [
          { title: 'PLAIN TEXT', value: 'TextField' },
          { title: 'LONG TEXT', value: 'LongText' },
          { title: 'RICH TEXT', value: 'RichText' },
        ],
        defaultValue: () => get(config, 'sub_type', 'TextField'),
        props: { exclusive: true, label: 'Text Type' },
      });
    }

    if (fieldType === 'Select') {
      inputs.push(
        {
          id: 'config.allow_multiple',
          type: 'ToggleButton',
          options: [
            { title: 'YES', value: 'yes' },
            { title: 'NO', value: 'no' },
          ],
          defaultValue: () => toBoolStr(get(config, 'allow_multiple', false)),
          props: { exclusive: true, label: 'Allow Multiple Selections' },
        },
        {
          id: 'config.allow_custom_input',
          type: 'ToggleButton',
          options: [
            { title: 'YES', value: 'yes' },
            { title: 'NO', value: 'no' },
          ],
          defaultValue: () => toBoolStr(get(config, 'allow_custom_input', false)),
          props: { exclusive: true, label: 'Allow Custom Input' },
        },
        {
          id: 'config.sub_type',
          type: 'Select',
          defaultValue: () => get(config, 'sub_type', 'Select'),
          data: resolveSelectSubType(
            get(config, 'allow_multiple', false),
            get(config, 'allow_custom_input', false)
          ).map((st) => ({ title: SELECT_SUB_TYPE_LABELS[st] || st, value: st })),
          props: { label: 'Field Sub-type' },
        },
        {
          id: 'config.options',
          type: 'ChipAdd',
          defaultValue: () => get(config, 'options', []).map((o) => o.label || o),
          props: { label: 'Options (add each option)' },
        }
      );
    }

    if (fieldType === 'Toggle') {
      inputs.push({
        id: 'config.options',
        type: 'ChipAdd',
        defaultValue: () => get(config, 'options', []).map((o) => o.title || o),
        props: { label: 'Toggle Options (enter 2 options)', maxItems: 2 },
      });
    }

    if (fieldType === 'Media') {
      inputs.push({
        id: 'config.sub_type',
        type: 'ToggleButton',
        options: [
          { title: 'IMAGES', value: 'DropArea' },
          { title: 'FILES', value: 'DropZone' },
          { title: 'BOTH', value: 'Both' },
        ],
        defaultValue: () => get(config, 'sub_type', 'DropArea'),
        props: { exclusive: true, label: 'Media Type' },
      });
    }

    return inputs;
  };

  return (
    <div style={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 4, padding: 12, marginBottom: 12 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 12 }}>
        <IconButton size="small" onClick={onRemove} style={{ color: theme.palette.secondary.main, marginTop: 8 }}>
          <Icon>close</Icon>
        </IconButton>
        <FormControl variant="outlined" size="small" style={{ minWidth: 140 }}>
          <InputLabel>Field Type</InputLabel>
          <Select
            value={fieldType}
            label="Field Type"
            onChange={(e) => {
              const newType = e.target.value;
              const newConfig = newType === 'Select'
                ? { allow_multiple: false, allow_custom_input: false, sub_type: 'Select', options: [] }
                : {};
              onChange(index, { ...field, type: newType, config: newConfig });
            }}
          >
            {NESTED_FIELD_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>{t.title}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <Form key={fieldType} showAllScreens onFormState={handleFormState} screens={[{ inputs: getNestedInputs() }]} />
    </div>
  );
};

// --- Repeater config panel ---
const RepeaterConfig = ({ state, setState, error }) => {
  const theme = useTheme();
  const nestedFields = get(state, 'config.nested_fields', []);

  const updateNested = (index, updated) => {
    const fields = [...nestedFields];
    fields[index] = updated;
    const next = deepClone(state);
    set(next, 'config.nested_fields', fields);
    setState(next);
  };

  const removeNested = (index) => {
    const fields = [...nestedFields];
    fields.splice(index, 1);
    const next = deepClone(state);
    set(next, 'config.nested_fields', fields);
    setState(next);
  };

  const addNested = () => {
    const fields = [...nestedFields, { type: 'Text', label: '', config: {} }];
    const next = deepClone(state);
    set(next, 'config.nested_fields', fields);
    setState(next);
  };

  const updateConfig = (key, value) => {
    const next = deepClone(state);
    set(next, `config.${key}`, value);
    setState(next);
  };

  return (
    <div style={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 4, padding: 16, marginTop: 16 }}>
      <TextField
        variant="outlined"
        size="small"
        fullWidth
        label="Helper Text"
        value={get(state, 'config.helper_text', '')}
        onChange={(e) => updateConfig('helper_text', e.target.value)}
        style={{ marginBottom: 16 }}
      />
      <TextField
        variant="outlined"
        size="small"
        fullWidth
        label="Repeater Item Name"
        value={get(state, 'config.item_name', '')}
        onChange={(e) => updateConfig('item_name', e.target.value)}
        style={{ marginBottom: 4 }}
      />
      <Text variant="caption" color="textSecondary" style={{ display: 'block', marginBottom: 16 }}>
        This name will appear above each item followed by a number. Like, if &quot;Step&quot; is set, items will be labeled &quot;Step 1&quot;, &quot;Step 2&quot;, etc.
      </Text>

      <fieldset style={{ border: `1px solid ${error ? theme.palette.error.main : theme.palette.divider}`, borderRadius: 4, padding: 12, margin: 0 }}>
        <legend style={{ fontSize: 13, color: error ? theme.palette.error.main : theme.palette.text.secondary, padding: '0 4px' }}>Repeater Fields</legend>
        {error && (
          <Text variant="caption" style={{ color: theme.palette.error.main, display: 'block', marginBottom: 8 }}>
            At least one field is required
          </Text>
        )}

        <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
          <Text variant="caption" style={{ minWidth: 140, marginLeft: 40 }}>Field Type</Text>
          <Text variant="caption" style={{ flex: 1 }}>Label</Text>
        </div>

        {nestedFields.map((nf, i) => (
          <NestedFieldConfig
            key={i}
            field={nf}
            index={i}
            onChange={updateNested}
            onRemove={() => removeNested(i)}
          />
        ))}

        <Button
          color="primary"
          fullWidth
          startIcon={<Icon>add</Icon>}
          onClick={addNested}
          style={{ marginTop: 8 }}
        >
          ADD FIELD
        </Button>
      </fieldset>
    </div>
  );
};

// --- Main config modal ---
const TaskTypeFieldConfig = ({ open, field, onSave, onClose }) => {
  const [state, setState] = useState(deepClone(field));
  const formValsRef = useRef({});
  const formRef = useRef(null);

  const catEntry = getCategoryByComponent(field.field_type);
  const catKey = catEntry ? catEntry[0] : 'Text';

  // Store form values in a ref to avoid re-rendering the Form (which resets toggles)
  const onFormState = useCallback((vals) => {
    formValsRef.current = { ...formValsRef.current, ...vals };
  }, []);

  const [labelError, setLabelError] = useState(false);
  const [repeaterError, setRepeaterError] = useState(false);

  const handleSave = () => {
    // Read latest form state synchronously via the Form ref to avoid debounce lag
    const latestFormState = get(formRef, 'current.formState', formValsRef.current);
    const source = { ...formValsRef.current, ...latestFormState };
    // Merge form values into state for final save
    const updated = deepClone(state);
    Object.keys(source).forEach((k) => {
      // Deep-clone arrays/objects to avoid stale references from in-place mutations
      const val = source[k];
      set(updated, k, Array.isArray(val) ? [...val] : val);
    });
    // Validate label is not empty
    if (!get(updated, 'label', '').trim()) {
      setLabelError(true);
      return;
    }
    setLabelError(false);
    // Validate repeater has at least one nested field
    if (catKey === 'Repeater') {
      const nestedFields = get(updated, 'config.nested_fields', []);
      if (!nestedFields.length) {
        setRepeaterError(true);
        return;
      }
      setRepeaterError(false);
    }
    // Convert string toggle values back to booleans
    const boolFields = ['is_required', 'config.allow_multiple', 'config.allow_custom_input', 'config.use_range', 'config.allow_previous_dates'];
    boolFields.forEach((path) => {
      const v = get(updated, path);
      if (v === 'yes' || v === 'no') set(updated, path, fromBoolStr(v));
    });
    // Convert default_date 'none' back to empty string
    if (get(updated, 'config.default_date') === 'none') set(updated, 'config.default_date', '');
    if (catKey === 'Select') {
      const allowMultiple = get(updated, 'config.allow_multiple', false);
      const allowCustom = get(updated, 'config.allow_custom_input', false);
      const subTypes = resolveSelectSubType(allowMultiple, allowCustom);
      const currentSubType = get(updated, 'config.sub_type');
      if (!subTypes.includes(currentSubType)) {
        set(updated, 'config.sub_type', subTypes[0]);
      }
    }
    onSave(updated);
  };

  const getConfigInputs = () => {
    const inputs = [
      {
        id: 'label',
        type: 'TextField',
        required: true,
        defaultValue: () => get(state, 'label', ''),
        props: { label: 'Field Name', variant: 'outlined', error: labelError, helperText: labelError ? 'Field name is required' : undefined },
      },
      {
        id: 'is_required',
        type: 'ToggleButton',
        options: [
          { title: 'REQUIRED', value: 'yes' },
          { title: 'OPTIONAL', value: 'no' },
        ],
        defaultValue: () => toBoolStr(get(state, 'is_required', false)),
        props: { exclusive: true, label: 'Required' },
      },
    ];

    if (catKey === 'Toggle') {
      inputs.push({
        id: 'config.options',
        type: 'ChipAdd',
        defaultValue: () => get(state, 'config.options', []).map((o) => o.title),
        props: { label: 'Toggle Options (enter 2 options)', maxItems: 2 },
      });
    }

    if (catKey === 'Select') {
      inputs.push(
        {
          id: 'config.allow_multiple',
          type: 'ToggleButton',
          options: [
            { title: 'YES', value: 'yes' },
            { title: 'NO', value: 'no' },
          ],
          defaultValue: () => toBoolStr(get(state, 'config.allow_multiple', false)),
          props: { exclusive: true, label: 'Allow Multiple Selections' },
        },
        {
          id: 'config.allow_custom_input',
          type: 'ToggleButton',
          options: [
            { title: 'YES', value: 'yes' },
            { title: 'NO', value: 'no' },
          ],
          defaultValue: () => toBoolStr(get(state, 'config.allow_custom_input', false)),
          props: { exclusive: true, label: 'Allow Custom Input' },
        },
        {
          id: 'config.sub_type',
          type: 'Select',
          defaultValue: () => get(state, 'config.sub_type', 'Select'),
          data: () => {
            const fs = get(formRef, 'current.formState', {});
            const amRaw = get(fs, 'config.allow_multiple');
            const acRaw = get(fs, 'config.allow_custom_input');
            const allowMultiple = amRaw !== undefined ? fromBoolStr(amRaw) : get(state, 'config.allow_multiple', false);
            const allowCustom = acRaw !== undefined ? fromBoolStr(acRaw) : get(state, 'config.allow_custom_input', false);
            return resolveSelectSubType(allowMultiple, allowCustom).map((st) => ({
              title: SELECT_SUB_TYPE_LABELS[st] || st,
              value: st,
            }));
          },
          props: { label: 'Display Style' },
        },
        {
          id: 'config.options',
          type: 'ChipAdd',
          defaultValue: () => get(state, 'config.options', []).map((o) => o.label || o),
          props: { label: 'Options (add each option)' },
        }
      );
    }

    if (catKey === 'Text') {
      inputs.push(
        {
          id: 'config.sub_type',
          type: 'ToggleButton',
          options: [
            { title: 'TEXT', value: 'TextField' },
            { title: 'LONG TEXT', value: 'LongText' },
            { title: 'RICH TEXT', value: 'RichText' },
          ],
          defaultValue: () => get(state, 'config.sub_type', 'TextField'),
          props: { exclusive: true, label: 'Text Type' },
        },
        {
          id: 'config.prompt_text',
          type: 'TextField',
          defaultValue: () => get(state, 'config.prompt_text', ''),
          props: { label: 'Prompt Text (placeholder)' },
        },
        {
          id: 'config.default_text',
          type: 'TextField',
          defaultValue: () => get(state, 'config.default_text', ''),
          props: { label: 'Default Text' },
        }
      );
    }

    if (catKey === 'Date') {
      inputs.push(
        {
          id: 'config.use_range',
          type: 'ToggleButton',
          options: [
            { title: 'YES', value: 'yes' },
            { title: 'NO', value: 'no' },
          ],
          defaultValue: () => toBoolStr(get(state, 'config.use_range', false)),
          props: { exclusive: true, label: 'Use Date Range' },
        },
        {
          id: 'config.allow_previous_dates',
          type: 'ToggleButton',
          options: [
            { title: 'YES', value: 'yes' },
            { title: 'NO', value: 'no' },
          ],
          defaultValue: () => toBoolStr(get(state, 'config.allow_previous_dates', true)),
          props: { exclusive: true, label: 'Allow Previous Dates' },
        },
        {
          id: 'config.default_date',
          type: 'ToggleButton',
          options: [
            { title: 'NONE', value: 'none' },
            { title: 'TODAY', value: 'today' },
          ],
          defaultValue: () => get(state, 'config.default_date', '') === 'today' ? 'today' : 'none',
          props: { exclusive: true, label: 'Default Date' },
        }
      );
    }

    if (catKey === 'Media') {
      inputs.push(
        {
          id: 'config.sub_type',
          type: 'ToggleButton',
          options: [
            { title: 'IMAGES', value: 'DropArea' },
            { title: 'FILES', value: 'DropZone' },
            { title: 'BOTH', value: 'Both' },
          ],
          defaultValue: () => get(state, 'config.sub_type', 'DropArea'),
          props: { exclusive: true, label: 'Media Type' },
        },
        {
          id: 'config.max_allowed',
          type: 'TextField',
          defaultValue: () => get(state, 'config.max_allowed', 10),
          props: { label: 'Max Files Allowed', type: 'number' },
        }
      );
    }

    return inputs;
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Configure Field"
      buttons={[
        { title: 'Cancel', onClick: onClose, variant: 'outlined' },
        { title: 'Save', onClick: handleSave, variant: 'contained' },
      ]}
    >
      <ConfigForm>
        <Form ref={formRef} showAllScreens onFormState={onFormState} screens={[{ inputs: getConfigInputs() }]} />
        {catKey === 'Repeater' && <RepeaterConfig state={state} setState={(s) => { setRepeaterError(false); setState(s); }} error={repeaterError} />}
      </ConfigForm>
    </Modal>
  );
};

export default TaskTypeFieldConfig;
